import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  Divider,
  LinearProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Switch,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InfoIcon from '@mui/icons-material/Info';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx'; 

const FileDetailsPage = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const { getFileDetails } = AiActions();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState<
    'All' | 'Completed' | 'Processing' | 'Failed' | 'Pending'
  >('All');
  const [searchQuery, setSearchQuery] = useState('');

  const { file_id, knowledge_base_id, organization_id, fileName, knowledgeBaseName } =
    location.state || {};

  const [fileDetails, setFileDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchFileDetails = async () => {
    if (!file_id || !knowledge_base_id || !organization_id) {
      showSnackBar('Missing required parameters', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await getFileDetails(organization_id, knowledge_base_id, file_id);
      setFileDetails(response);
    } catch (err) {
      showSnackBar('Failed to load file details', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFileDetails();
  }, [file_id, knowledge_base_id, organization_id]);

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => navigate('/'),
    },
    {
      label: 'Knowledge Base List',
      onClick: () => navigate('/r/vector/add-knowledge'),
    },
    {
      label: knowledgeBaseName || 'Details',
      onClick: () =>
        navigate('/r/vector/knowledge-base/details', {
          state: { knowledge_base_id, organization_id },
        }),
    },
    {
      label: 'File Details',
    },
  ];

  const getFileTypeIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'xlsx':
      case 'xls':
        return '📊';
      case 'docx':
      case 'doc':
        return '📝';
      case 'txt':
        return '📃';
      case 'csv':
        return '📈';
      default:
        return '📁';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
        return 'warning';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    return `${diffMins}m ${diffSecs}s`;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography variant="h6">Loading File Details...</Typography>
        </Box>
      </Box>
    );
  }

  if (!fileDetails) {
    return (
      <Box sx={{ p: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Alert severity="error">
          File details not found. The file may have been deleted or you may not have permission to
          access it.
        </Alert>
      </Box>
    );
  }

  const chunkStats = fileDetails.chunk_statistics || {};
  const completionPercentage = chunkStats.total_chunks
    ? Math.round((chunkStats.completed_chunks / chunkStats.total_chunks) * 100)
    : 0;

  const chunks = fileDetails.chunks || [];
  const filteredChunks = chunks.filter((c: any) => {
    const statusOk =
      statusFilter === 'All' || (c?.status || '').toLowerCase() === statusFilter.toLowerCase();
    const text = (c?.chunk_text_preview || '').toLowerCase();
    const searchOk = !searchQuery || text.includes(searchQuery.toLowerCase());
    return statusOk && searchOk;
  });

  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Breadcrumb */}
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => item?.onClick?.()}
      />

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        mt={1}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Box sx={{ fontSize: '2rem' }}>{getFileTypeIcon(fileDetails.file_type)}</Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: '#1e293b' }}>
                {fileDetails.file_name}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* File Information */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 'fit-content' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <InfoIcon color="primary" />
              File Information
            </Typography>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  File ID
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <FingerprintIcon fontSize="small" color="action" />
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                    {fileDetails.file_id}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  File Type
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {fileDetails.file_type?.toUpperCase() || 'N/A'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  File Size
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatFileSize(fileDetails.file_size)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  File Hash
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                  {fileDetails.file_hash?.substring(0, 16)}...
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  File ID
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                  {fileDetails.file_id?.substring(0, 16)}...
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  File Name
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                  {fileDetails.file_name?.substring(0, 16)}...
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Uploaded
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {fileDetails.created_at
                    ? new Date(fileDetails.created_at).toLocaleString()
                    : 'N/A'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {fileDetails.updated_at
                    ? new Date(fileDetails.updated_at).toLocaleString()
                    : 'N/A'}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Processing Time
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDuration(fileDetails.created_at, fileDetails.updated_at)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Processing Configuration */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, borderRadius: 2, height: 'fit-content' }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <StorageIcon color="primary" />
              Processing Configuration
            </Typography>
            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Chunk Size
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {fileDetails.chunk_size || 'N/A'} characters
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Chunk Overlap
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {fileDetails.chunk_overlap || 'N/A'} characters
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Knowledge Base ID
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                  {fileDetails.knowledge_base_id}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Organization ID
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                  {fileDetails.organization_id}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Chunk Statistics */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <AssessmentIcon color="primary" />
              Processing Statistics
            </Typography>

            {/* Progress Overview */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Processing Progress
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {completionPercentage}% Complete
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            {/* Detailed Statistics */}
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: '#0ea5e9', mb: 1 }} />
                  <Typography variant="h4" fontWeight={700} color="#0ea5e9">
                    {chunkStats.total_chunks || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Chunks
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 32, color: '#22c55e', mb: 1 }} />
                  <Typography variant="h4" fontWeight={700} color="#22c55e">
                    {chunkStats.completed_chunks || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" sx={{ p: 2, bgcolor: '#fef3c7', borderRadius: 2 }}>
                  <HourglassEmptyIcon sx={{ fontSize: 32, color: '#f59e0b', mb: 1 }} />
                  <Typography variant="h4" fontWeight={700} color="#f59e0b">
                    {chunkStats.processing_chunks || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Processing
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 2 }}>
                  <ErrorIcon sx={{ fontSize: 32, color: '#ef4444', mb: 1 }} />
                  <Typography variant="h4" fontWeight={700} color="#ef4444">
                    {chunkStats.failed_chunks || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Failed
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {chunks.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Chunks ({chunks.length})
              </Typography>

              {/* Scroll only when there are more than 5 chunks */}
              <Box
                sx={{
                  maxHeight: chunks.length > 5 ? 520 : 'auto',
                  overflowY: chunks.length > 5 ? 'auto' : 'visible',
                  pr: chunks.length > 5 ? 1 : 0,
                }}
              >
                {chunks.map((chunk: any, idx: number) => {
                  const pad2 = (n: number | string) => String(n ?? '').padStart(2, '0');
                  const status = (chunk?.status || '').toLowerCase();
                  const enabled = Boolean(chunk?.pinecone_uploaded && status === 'completed');

                  const dotColor =
                    status === 'completed'
                      ? 'success.main'
                      : status === 'processing'
                        ? 'warning.main'
                        : status === 'failed'
                          ? 'error.main'
                          : status === 'pending'
                            ? 'info.main'
                            : 'text.disabled';

                  return (
                    <Box
                      key={chunk?.chunk_id || idx}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {/* Top row: title + enabled dot */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        flexWrap="wrap"
                      >
                        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                          <Typography variant="subtitle2" fontWeight={700}>
                            {`Chunk-${pad2(chunk?.chunk_index ?? idx)}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {chunk?.chunk_size ?? 0} characters
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            • 0 Retrieval count
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="text.secondary">
                            {enabled ? 'Enabled' : 'Disabled'}
                          </Typography>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: enabled ? 'success.main' : 'text.disabled',
                            }}
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.25 }} />

                      {/* Preview */}
                      <Box sx={{ mb: 1 }}>
                        {/* <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {chunk?.chunk_text_preview
                    ? (chunk.chunk_text_preview.split('\n')[0] || 'Preview')
                    : 'Preview'}
                </Typography> */}
                        <Typography variant="body2" color="text.secondary">
                          {chunk?.chunk_text_preview || '—'}
                        </Typography>
                      </Box>

                      {/* Meta chips */}
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`Status: ${(chunk?.status || 'N/A').toUpperCase()}`}
                          color={getStatusColor(chunk?.status) as any}
                        />
                        <Box display="inline-flex" alignItems="center" gap={0.5}>
                          <Box
                            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {status || 'unknown'}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={`Vector: ${(chunk?.vector_id || '').slice(0, 12)}…`}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Chunk ID: ${(chunk?.chunk_id || '').slice(0, 12)}…`}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Created: ${
                            chunk?.created_at ? new Date(chunk.created_at).toLocaleString() : 'N/A'
                          }`}
                          variant="outlined"
                        />
                        <Chip
                          size="small"
                          label={`Updated: ${
                            chunk?.updated_at ? new Date(chunk.updated_at).toLocaleString() : 'N/A'
                          }`}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* No Chunks Available Notice */}
        {!fileDetails.chunks && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: '#f8fafc' }}>
              <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Chunk Content Not Available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
                The individual chunk contents are not returned by the API for performance reasons.
                However, all {chunkStats.total_chunks || 0} chunks have been successfully processed
                and are available for search and retrieval.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default FileDetailsPage;
